import axios from 'axios';

// Attach the signed access token to every request so the API verifies identity
// server-side (replaces the old "trust the userid in the body" model). One place
// covers every axios call in the app.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Session renewal (2026-10-13, Helena: "remember the login on their phone … they don't have to do their
// login details again"). The bearer token lives 12 h; the 30-day refresh token (minted when "Remember this
// device" is ticked — the default) only ever renewed it on a cold page load (AuthProvider). A phone that keeps
// the tab open overnight therefore started getting 401 "Invalid or expired session" and bounced to sign-in.
// Now: on a 401 from the API, with a refresh token at hand, renew ONCE (single-flight) and retry the request
// with the new token. A refresh that the server rejects leaves the error to the caller (and the existing
// sign-in flow); /login/* calls are never retried.
// 2026-10-15 (blank-screen fix): renewal also covers /login/get--user-access (the account load after sign-in,
// which the old /login/ exclusion skipped — an expired token there left a signed-in user with no suppliers, i.e.
// a blank app). When the server REFUSES to renew (switched off, revoked, expired, or no refresh token), the session
// is over: a 'spearbi:session-ended' event tells AuthProvider to clear it and show the sign-in page with a message
// instead of leaving blank pages. A network failure during renewal is NOT a refusal — the session is kept.
export const SESSION_ENDED_EVENT = 'spearbi:session-ended';
const endSession = (reason) => {
  try { window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason } })); } catch (e) { /* ignore */ }
};

let renewing = null;
// Resolves { token } on success, { refused: true } when the server said no, {} on a transport failure.
const renewSession = () => {
  if (renewing) return renewing;
  const refreshToken = localStorage.getItem('refreshToken');
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) return Promise.resolve({});
  if (!refreshToken) return Promise.resolve({ refused: true });
  renewing = axios
    .post(`${apiUrl}/login/refresh`, { refreshToken }, { _skipRenew: true })
    .then((r) => {
      const row = r.status === 201 ? r.data?.data?.[0] : null;
      if (!row || !r.data?.accessToken) return { refused: true };
      localStorage.setItem('accessToken', r.data.accessToken);
      if (r.data.refreshToken) localStorage.setItem('refreshToken', r.data.refreshToken);
      return { token: r.data.accessToken };
    })
    .catch(() => ({}))
    .finally(() => { renewing = null; });
  return renewing;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const url = String(cfg?.url || '');
    const isLoginCall = url.indexOf('/login/') !== -1 && url.indexOf('/login/get--user-access') === -1;
    if (status !== 401 || !cfg || cfg._skipRenew || isLoginCall || url.indexOf('/reports/api/v1') === -1) {
      return Promise.reject(error);
    }
    const inactive = error?.response?.data?.code === 'ACCOUNT_INACTIVE';
    if (cfg._renewed) {
      endSession(inactive ? 'inactive' : 'expired');
      return Promise.reject(error);
    }
    const { token, refused } = await renewSession();
    if (!token) {
      if (refused) endSession(inactive ? 'inactive' : 'expired');
      return Promise.reject(error);
    }
    cfg._renewed = true;
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
    return axios(cfg);
  },
);

// CSV / file-export endpoints use the native fetch() API, which the axios
// interceptor above does not touch. Wrap fetch once so those calls also carry
// the bearer token. Scoped to our API host; leaves the caller's own
// Authorization header (if any) intact.
if (typeof window !== 'undefined' && window.fetch) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const token = localStorage.getItem('accessToken');
    if (token && url.indexOf('/reports/api/v1') !== -1) {
      const headers = new Headers(init.headers || (typeof input !== 'string' && input.headers) || {});
      if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      init = { ...init, headers };
    }
    return _origFetch(input, init);
  };
}

// The DEMO supplier is an ordinary supplier with its own synthetic data (backend
// docs/michael/demo-revamp/TASK-demo-revamp.md); the former *_demo URL rewrite is gone.
